/**
 * NavigatorX SuperAdmin Panel - Master Dashboard
 * 
 * Enterprise-grade admin panel integrating all NavigatorX management:
 * - User Management with bulk actions
 * - Billing & Subscriptions
 * - Referral Program with Tree Visualization
 * - Voice Cloning Management & Analytics
 * - Churn / Cancellation Analysis
 * - Push Notification Analytics
 * - Well Data Management
 * - Hazard Moderation
 * - App Configuration
 * - Real-time Analytics & Reporting
 * - Customer Support
 * - Audit & Security
 * - System Health
 * - Waitlist Management
 * 
 * @author Claude AI for TGE.LA
 * @version 1.3.0 - Added full analytics suite (Notifications, Voices, Churn, Referral Tree)
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import {
  Users,
  CreditCard,
  Gift,
  Database,
  AlertTriangle,
  Settings,
  BarChart3,
  HeadphonesIcon,
  Shield,
  Activity,
  UserPlus,
  Menu,
  X,
  ChevronRight,
  Loader2,
  Bell,
  Search,
  RefreshCw,
  Mic,
  XCircle,
  Ticket,
  ShieldBan,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { auth, navDb } from '../../utils/firebase/client';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';

// Import all components
import {
  AnalyticsDashboard,
  ReferralDashboard,
  FraudDetectionDashboard,
  WellDataManager,
  HazardManager,
  AppConfigManager,
  RealTimeAnalyticsDashboard,
  SupportDashboard,
  AuditSecurityDashboard,
  SystemHealthDashboard,
  WaitlistManager,
  VoiceCloningDashboard,
  CancellationAnalysis,
  NotificationAnalytics,
  ReferralTreeViewer,
  VoiceAnalytics,
  PresetVoiceCreator,
  PromoCodeManager,
  UserManagement,
  GdprComplianceDashboard,
  BlocklistManager,
  FleetManager
} from './navigatorx';

// Collection constants
const NAV_USERS_COLLECTION = 'nav_users';
const TICKETS_COLLECTION = 'nav_support_tickets';
const HAZARDS_COLLECTION = 'hazards';
const WAITLIST_COLLECTION = 'waitlist';
const ALERTS_COLLECTION = 'nav_security_alerts';

// Types
interface NavigatorXAdminPanelProps {
  initialTab?: string;
}

type AdminSection =
  | 'overview'
  | 'fleet'
  | 'users'
  | 'billing'
  | 'referrals'
  | 'voices'
  | 'churn'
  | 'notifications'
  | 'wells'
  | 'hazards'
  | 'config'
  | 'analytics'
  | 'support'
  | 'security'
  | 'health'
  | 'waitlist'
  | 'promos'
  | 'gdpr'
  | 'blocklist';

interface NavItem {
  id: AdminSection;
  label: string;
  icon: any;
  description: string;
  badge?: number;
  badgeType?: 'info' | 'warning' | 'error';
}

interface QuickStats {
  totalUsers: number;
  activeToday: number;
  openTickets: number;
  pendingHazards: number;
  waitlistCount: number;
  securityAlerts: number;
  voiceClones: number;
  failedVoices: number;
}

export default function NavigatorXAdminPanel({ initialTab = 'overview' }: NavigatorXAdminPanelProps) {
  const [activeSection, setActiveSection] = useState<AdminSection>(initialTab as AdminSection);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Quick stats for overview
  const [quickStats, setQuickStats] = useState<QuickStats>({
    totalUsers: 0,
    activeToday: 0,
    openTickets: 0,
    pendingHazards: 0,
    waitlistCount: 0,
    securityAlerts: 0,
    voiceClones: 0,
    failedVoices: 0
  });

  // Real system status checks for Overview tab
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, 'operational' | 'checking' | 'error'>>({
    Firebase: 'checking',
    'Well DBs': 'checking',
  });

  // Check real Firebase connectivity for the Overview System Status card
  useEffect(() => {
    async function checkServices() {
      const statuses: Record<string, 'operational' | 'checking' | 'error'> = {};

      // Firestore check — read a lightweight doc
      try {
        await getDoc(doc(navDb, 'nav_config', 'app_settings'));
        statuses['Firebase'] = 'operational';
      } catch {
        statuses['Firebase'] = 'error';
      }

      // Well databases — check if nav_data_sources has entries
      try {
        const snapshot = await getDocs(collection(navDb, 'nav_data_sources'));
        statuses['Well DBs'] = snapshot.size > 0 ? 'operational' : 'error';
      } catch {
        statuses['Well DBs'] = 'error';
      }

      setServiceStatuses(statuses);
    }
    checkServices();
  }, []);

  // Validate admin authentication
  const adminId = auth.currentUser?.uid;
  const isAuthenticated = Boolean(adminId);

  // Load real stats from Firestore
  const loadQuickStats = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Fetch stats in parallel with error handling for each
      const [usersSnap, ticketsSnap, hazardsSnap, waitlistSnap, alertsSnap] = await Promise.allSettled([
        getDocs(collection(navDb, NAV_USERS_COLLECTION)),
        getDocs(query(collection(navDb, TICKETS_COLLECTION), where('status', '==', 'open'))),
        getDocs(query(collection(navDb, HAZARDS_COLLECTION), where('status', '==', 'pending'))),
        getDocs(collection(navDb, WAITLIST_COLLECTION)),
        getDocs(query(collection(navDb, ALERTS_COLLECTION), where('acknowledged', '==', false)))
      ]);

      // Extract counts safely
      const totalUsers = usersSnap.status === 'fulfilled' 
        ? usersSnap.value.docs.filter(d => !['blocked', 'invites', 'admin_logs'].includes(d.id)).length 
        : 0;
      
      const openTickets = ticketsSnap.status === 'fulfilled' 
        ? ticketsSnap.value.size 
        : 0;
      
      const pendingHazards = hazardsSnap.status === 'fulfilled' 
        ? hazardsSnap.value.size 
        : 0;
      
      const waitlistCount = waitlistSnap.status === 'fulfilled' 
        ? waitlistSnap.value.size 
        : 0;
      
      const securityAlerts = alertsSnap.status === 'fulfilled' 
        ? alertsSnap.value.size 
        : 0;

      // Calculate active today
      let activeToday = 0;
      if (usersSnap.status === 'fulfilled') {
        usersSnap.value.docs.forEach(doc => {
          const data = doc.data();
          if (data.lastActiveAt) {
            try {
              const lastActive = data.lastActiveAt.toDate();
              if (lastActive >= today) activeToday++;
            } catch {
              // Skip invalid dates
            }
          }
        });
      }

      setQuickStats({
        totalUsers,
        activeToday,
        openTickets,
        pendingHazards,
        waitlistCount,
        securityAlerts,
        voiceClones: 0, // Loaded separately by VoiceCloningDashboard
        failedVoices: 0 // Loaded separately by VoiceCloningDashboard
      });
    } catch (error) {
      console.error('Error loading quick stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadQuickStats();
    
    // Refresh stats every 2 minutes
    const interval = setInterval(loadQuickStats, 120000);
    return () => clearInterval(interval);
  }, [loadQuickStats]);

  const navItems: NavItem[] = [
    { id: 'overview', label: 'Overview', icon: Activity, description: 'Dashboard overview' },
    { id: 'fleet', label: 'Fleet', icon: Truck, description: 'Company fleet accounts' },
    { id: 'users', label: 'Users', icon: Users, description: 'User management', badge: quickStats.totalUsers },
    { id: 'billing', label: 'Billing', icon: CreditCard, description: 'Subscriptions & revenue' },
    { id: 'promos', label: 'Promo Codes', icon: Ticket, description: 'Promotional codes & discounts' },
    { id: 'referrals', label: 'Referrals', icon: Gift, description: 'Referral program' },
    { id: 'voices', label: 'Voice Clones', icon: Mic, description: 'Voice cloning management', badge: quickStats.failedVoices, badgeType: quickStats.failedVoices > 0 ? 'warning' : undefined },
    { id: 'churn', label: 'Churn Analysis', icon: XCircle, description: 'Cancellation insights' },
    { id: 'notifications', label: 'Notifications', icon: Bell, description: 'Push notification analytics' },
    { id: 'wells', label: 'Well Data', icon: Database, description: 'Oklahoma well data management' },
    { id: 'hazards', label: 'Hazards', icon: AlertTriangle, description: 'Hazard moderation', badge: quickStats.pendingHazards, badgeType: quickStats.pendingHazards > 0 ? 'warning' : undefined },
    { id: 'config', label: 'App Config', icon: Settings, description: 'Feature flags & settings' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, description: 'Usage & metrics' },
    { id: 'support', label: 'Support', icon: HeadphonesIcon, description: 'Customer support', badge: quickStats.openTickets, badgeType: quickStats.openTickets > 10 ? 'error' : 'info' },
    { id: 'security', label: 'Security', icon: Shield, description: 'Audit & compliance', badge: quickStats.securityAlerts, badgeType: quickStats.securityAlerts > 0 ? 'error' : undefined },
    { id: 'health', label: 'System Health', icon: Activity, description: 'Monitoring & status' },
    { id: 'waitlist', label: 'Waitlist', icon: UserPlus, description: 'Pre-launch signups', badge: quickStats.waitlistCount },
    { id: 'gdpr', label: 'GDPR', icon: Shield, description: 'Data export & deletion requests' },
    { id: 'blocklist', label: 'Blocklist', icon: ShieldBan, description: 'Blocked emails & phones' }
  ];

  const getBadgeColor = (type?: string) => {
    switch (type) {
      case 'error': return 'bg-red-500';
      case 'warning': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  const renderContent = () => {
    const contentMap: Record<AdminSection, JSX.Element> = {
      'overview': <OverviewDashboard stats={quickStats} statsLoading={statsLoading} onNavigate={setActiveSection} onRefresh={loadQuickStats} serviceStatuses={serviceStatuses} />,
      'fleet': <FleetManager embedded />,
      'users': <UserManagement embedded />,
      'billing': <AnalyticsDashboard embedded />,
      'promos': <PromoCodeManager embedded />,
      'referrals': <ReferralsSection />,
      'voices': <VoicesSection />,
      'churn': <CancellationAnalysis embedded />,
      'notifications': <NotificationAnalytics embedded />,
      'wells': <WellDataManager embedded />,
      'hazards': <HazardManager embedded />,
      'config': <AppConfigManager embedded />,
      'analytics': <RealTimeAnalyticsDashboard embedded />,
      'support': <SupportDashboard embedded />,
      'security': <AuditSecurityDashboard embedded />,
      'health': <SystemHealthDashboard embedded />,
      'waitlist': <WaitlistManager embedded />,
      'gdpr': <GdprComplianceDashboard embedded />,
      'blocklist': <BlocklistManager embedded />
    };

    const content = contentMap[activeSection] || contentMap['overview'];
    const sectionName = navItems.find(n => n.id === activeSection)?.label || 'Section';

    return (
      <ErrorBoundary componentName={sectionName}>
        {content}
      </ErrorBoundary>
    );
  };

  // Auth check
  if (!isAuthenticated && !loading) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in as an admin to access this panel.</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary componentName="NavigatorX Admin Panel">
      <div className="flex h-full bg-gray-100">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-gray-900 text-white transition-all duration-300 flex flex-col`}>
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-lg">NavigatorX</h1>
                <p className="text-xs text-gray-400">Admin Panel</p>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="sidebar-toggle" className="text-gray-400 hover:text-white">
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                data-testid={`sidebar-tab-${item.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  activeSection === item.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className={`px-2 py-0.5 text-xs rounded-full text-white ${getBadgeColor(item.badgeType)}`}>
                        {item.badge > 999 ? '999+' : item.badge}
                      </span>
                    )}
                  </>
                )}
              </button>
            ))}
          </nav>

          {sidebarOpen && (
            <div className="p-4 border-t border-gray-800 text-xs text-gray-500">
              <p>NavigatorX v1.0.0</p>
              <p>© 2026 OilWellX / TGE.LA</p>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <header className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-10">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {navItems.find(n => n.id === activeSection)?.label || 'Overview'}
              </h2>
              <p className="text-sm text-gray-500">
                {navItems.find(n => n.id === activeSection)?.description}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" placeholder="Search..." className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <Button variant="outline" size="sm" onClick={loadQuickStats} title="Refresh stats">
                <RefreshCw className={`h-4 w-4 ${statsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </header>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface OverviewDashboardProps {
  stats: QuickStats;
  statsLoading: boolean;
  onNavigate: (section: AdminSection) => void;
  onRefresh: () => void;
  serviceStatuses: Record<string, 'operational' | 'checking' | 'error'>;
}

function OverviewDashboard({ stats, statsLoading, onNavigate, onRefresh, serviceStatuses }: OverviewDashboardProps) {
  return (
    <div className="space-y-6">
      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          label="Total Users"
          value={stats.totalUsers}
          loading={statsLoading}
          icon={Users}
          iconColor="text-blue-400"
          onClick={() => onNavigate('users')}
        />
        <StatCard
          label="Active Today"
          value={stats.activeToday}
          loading={statsLoading}
          icon={Activity}
          iconColor="text-green-400"
          valueColor="text-green-600"
          onClick={() => onNavigate('analytics')}
        />
        <StatCard
          label="Open Tickets"
          value={stats.openTickets}
          loading={statsLoading}
          icon={HeadphonesIcon}
          iconColor="text-orange-400"
          valueColor="text-orange-600"
          onClick={() => onNavigate('support')}
        />
        <StatCard
          label="Pending Hazards"
          value={stats.pendingHazards}
          loading={statsLoading}
          icon={AlertTriangle}
          iconColor="text-yellow-400"
          valueColor="text-yellow-600"
          onClick={() => onNavigate('hazards')}
        />
        <StatCard
          label="Waitlist"
          value={stats.waitlistCount}
          loading={statsLoading}
          icon={UserPlus}
          iconColor="text-purple-400"
          valueColor="text-purple-600"
          onClick={() => onNavigate('waitlist')}
        />
        <StatCard
          label="Security Alerts"
          value={stats.securityAlerts}
          loading={statsLoading}
          icon={Shield}
          iconColor={stats.securityAlerts > 0 ? 'text-red-400' : 'text-gray-400'}
          valueColor={stats.securityAlerts > 0 ? 'text-red-600' : ''}
          highlight={stats.securityAlerts > 0}
          onClick={() => onNavigate('security')}
        />
      </div>

      {/* Quick Actions & Status */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button className="w-full justify-start" variant="outline" onClick={() => onNavigate('users')}>
              <Users className="h-4 w-4 mr-2" />
              Manage Users
              <ChevronRight className="h-4 w-4 ml-auto" />
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => onNavigate('billing')}>
              <CreditCard className="h-4 w-4 mr-2" />
              View Billing
              <ChevronRight className="h-4 w-4 ml-auto" />
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => onNavigate('config')}>
              <Settings className="h-4 w-4 mr-2" />
              App Config
              <ChevronRight className="h-4 w-4 ml-auto" />
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              {Object.entries(serviceStatuses).map(([service, status]) => (
                <div key={service} className="flex items-center justify-between">
                  <span>{service}</span>
                  <Badge className={
                    status === 'operational' ? 'bg-green-100 text-green-700' :
                    status === 'checking' ? 'bg-blue-100 text-blue-700' :
                    'bg-red-100 text-red-700'
                  }>
                    {status === 'checking' ? 'Checking...' : status === 'operational' ? 'Operational' : 'Error'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Analytics Preview</CardTitle>
              <Button variant="outline" size="sm" onClick={() => onNavigate('analytics')}>
                View Full
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ErrorBoundary componentName="Analytics Preview">
              <RealTimeAnalyticsDashboard embedded />
            </ErrorBoundary>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Reusable Stat Card
interface StatCardProps {
  label: string;
  value: number;
  loading: boolean;
  icon: any;
  iconColor: string;
  valueColor?: string;
  highlight?: boolean;
  onClick: () => void;
}

function StatCard({ label, value, loading, icon: Icon, iconColor, valueColor = '', highlight = false, onClick }: StatCardProps) {
  return (
    <Card 
      className={`cursor-pointer transition-colors ${highlight ? 'border-red-300 bg-red-50' : 'hover:border-blue-300'}`} 
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">{label}</p>
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin text-gray-300 mt-1" />
            ) : (
              <p className={`text-2xl font-bold ${valueColor}`}>{value.toLocaleString()}</p>
            )}
          </div>
          <Icon className={`h-8 w-8 ${iconColor}`} />
        </div>
      </CardContent>
    </Card>
  );
}


function ReferralsSection() {
  const [viewMode, setViewMode] = useState<'dashboard' | 'tree' | 'fraud'>('dashboard');
  
  return (
    <ErrorBoundary componentName="Referrals Section">
      <div className="space-y-6">
        <div className="flex gap-2 mb-4">
          <Button variant={viewMode === 'dashboard' ? 'default' : 'outline'} onClick={() => setViewMode('dashboard')}>
            Referral Dashboard
          </Button>
          <Button variant={viewMode === 'tree' ? 'default' : 'outline'} onClick={() => setViewMode('tree')}>
            Referral Tree
          </Button>
          <Button variant={viewMode === 'fraud' ? 'default' : 'outline'} onClick={() => setViewMode('fraud')}>
            Fraud Detection
          </Button>
        </div>
        
        {viewMode === 'dashboard' && <ReferralDashboard embedded />}
        {viewMode === 'tree' && <ReferralTreeViewer embedded />}
        {viewMode === 'fraud' && <FraudDetectionDashboard embedded />}
      </div>
    </ErrorBoundary>
  );
}

function VoicesSection() {
  const [viewMode, setViewMode] = useState<'manage' | 'analytics' | 'presets'>('manage');

  return (
    <ErrorBoundary componentName="Voices Section">
      <div className="space-y-6">
        <div className="flex gap-2 mb-4">
          <Button variant={viewMode === 'manage' ? 'default' : 'outline'} onClick={() => setViewMode('manage')}>
            <Mic className="h-4 w-4 mr-2" />
            User Voices
          </Button>
          <Button variant={viewMode === 'presets' ? 'default' : 'outline'} onClick={() => setViewMode('presets')}>
            <Mic className="h-4 w-4 mr-2" />
            Preset Voices
          </Button>
          <Button variant={viewMode === 'analytics' ? 'default' : 'outline'} onClick={() => setViewMode('analytics')}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Voice Analytics
          </Button>
        </div>

        {viewMode === 'manage' && <VoiceCloningDashboard embedded />}
        {viewMode === 'presets' && <PresetVoiceCreator embedded />}
        {viewMode === 'analytics' && <VoiceAnalytics embedded />}
      </div>
    </ErrorBoundary>
  );
}
